import React, { useState } from 'react';
import { motion } from 'framer-motion';

const FinalGateway = () => {
  const [form, setForm] = useState({ name: '', email: '' });
  const [submitted, setSubmitted] = useState(false);
  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });
  const handleSubmit = (e) => { e.preventDefault(); setSubmitted(true); };

  return (
    <section className="relative py-36 overflow-hidden bg-dark border-t border-gold/20">
      {/* Pulsing frame */}
      <motion.div
        animate={{ opacity: [0.3, 0.7, 0.3], scale: [1, 1.005, 1] }}
        transition={{ duration: 4, repeat: Infinity }}
        className="absolute inset-6 md:inset-12 pointer-events-none rounded-sm"
        style={{
          border: '1px solid rgba(212,175,55,0.25)',
          boxShadow: '0 0 60px rgba(212,175,55,0.08), inset 0 0 60px rgba(212,175,55,0.04)',
        }}
      />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_50%,rgba(212,175,55,0.07)_0%,transparent_65%)]" />

      <div className="relative z-10 max-w-3xl mx-auto px-6 text-center">
        <motion.p
          initial={{ opacity: 0, letterSpacing: '0.1em' }}
          whileInView={{ opacity: 1, letterSpacing: '0.4em' }}
          viewport={{ once: true }}
          transition={{ duration: 1.5 }}
          className="text-gold uppercase text-xs font-semibold mb-10"
          style={{ textShadow: '0 0 20px rgba(212,175,55,0.6)' }}
        >
          Das Portal · Deine Entscheidung
        </motion.p>

        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9 }}
          className="text-5xl md:text-7xl font-serif text-cream leading-tight mb-8"
        >
          Wie fühlt es sich an,{' '}
          <span
            className="block italic text-transparent bg-clip-text bg-gold-gradient mt-2"
            style={{ filter: 'drop-shadow(0 0 20px rgba(212,175,55,0.3))' }}
          >
            endlich frei zu sein?
          </span>
        </motion.h2>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4 }}
          className="space-y-6 text-lg text-cream/65 font-light leading-relaxed mb-10"
        >
          <p>
            Stell dir vor: Das Telefon bleibt stumm. Und es zerstört dich nicht mehr. Du
            liegst nachts in deinem Bett und spürst – zum ersten Mal seit langer Zeit –
            eine stille, unerschütterliche Ruhe in deiner Brust. Keine Angst. Kein Warten.
            Keine Enge. Nur du. Vollständig. Sicher. In dir selbst.
          </p>
          <p>
            Du gehst durch deinen Tag und merkst, dass du nicht mehr die Energie anderer
            scannst. Dass du nicht mehr deine Bedürfnisse streichst, bevor du sie
            überhaupt ausgesprochen hast. Dass du weißt, was du willst – und dass du
            bereit bist, genau das zu empfangen. Nicht weniger. Niemals wieder weniger.
          </p>
          <p className="text-cream font-serif italic text-xl">
            Die richtige Liebe fließt organisch zu dir – weil du aufgehört hast, sie
            wegzudrücken.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.6 }}
          className="mb-10 p-6 border border-wine/30"
          style={{ background: 'rgba(114,47,55,0.06)', borderRadius: '2px' }}
        >
          <p className="text-wine-light font-serif italic text-lg leading-relaxed">
            Bewirb dich nur für dieses Gespräch, wenn du bereit bist,{' '}
            <strong className="text-cream">die Illusion loszulassen</strong> und dich selbst zu wählen.
            Nicht ihn. Nicht die Beziehung. Dich.
          </p>
        </motion.div>

        {submitted ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="flex flex-col items-center gap-6 py-12"
          >
            <div
              className="w-20 h-20 rounded-full border-2 border-gold flex items-center justify-center text-gold text-3xl"
              style={{ boxShadow: '0 0 40px rgba(212,175,55,0.4)' }}
            >
              ✓
            </div>
            <h3 className="text-3xl font-serif text-cream">Dein Platz ist reserviert.</h3>
            <p className="text-cream/60 font-light max-w-md leading-relaxed">
              Jennifer meldet sich persönlich bei dir. Bereite dich auf deinen Durchbruch vor.
            </p>
          </motion.div>
        ) : (
          <motion.form
            onSubmit={handleSubmit}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.5 }}
            className="space-y-4 max-w-md mx-auto"
          >
            <input
              type="text"
              name="name"
              value={form.name}
              onChange={handleChange}
              placeholder="Dein Vorname"
              required
              className="w-full bg-white/5 border border-white/10 text-cream placeholder:text-cream/30 px-6 py-4 focus:outline-none focus:border-gold/60 transition-colors duration-500 font-light text-base rounded-sm"
            />
            <input
              type="email"
              name="email"
              value={form.email}
              onChange={handleChange}
              placeholder="Deine beste E-Mail-Adresse"
              required
              className="w-full bg-white/5 border border-white/10 text-cream placeholder:text-cream/30 px-6 py-4 focus:outline-none focus:border-gold/60 transition-colors duration-500 font-light text-base rounded-sm"
            />
            <motion.button
              type="submit"
              whileHover={{ scale: 1.04, boxShadow: '0 0 70px rgba(212,175,55,0.8), 0 0 140px rgba(212,175,55,0.4)' }}
              whileTap={{ scale: 0.97 }}
              className="w-full px-10 py-5 bg-gold-gradient text-dark font-bold uppercase tracking-widest text-sm relative overflow-hidden group"
              style={{ boxShadow: '0 0 35px rgba(212,175,55,0.5)', borderRadius: '2px' }}
            >
              <span className="relative z-10">✦ Jetzt GlücksCall anfragen →</span>
              <div className="absolute inset-0 bg-white/15 translate-x-[-110%] skew-x-[-20deg] group-hover:translate-x-[200%] transition-transform duration-1000 ease-in-out" />
            </motion.button>
            <p className="text-xs text-cream/30 tracking-widest text-center">
              Kostenlos · Kein Druck · Persönlich von Jennifer beantwortet
            </p>
          </motion.form>
        )}
      </div>
    </section>
  );
};

export default FinalGateway;